import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import {
  Users,
  UserCheck,
  BarChart3,
  Target,
  ClipboardCheck,
  TrendingUp,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navigationItems: NavItem[] = [
  {
    id: 'overview',
    label: 'Team Overview',
    icon: <Users size={20} />,
    path: '/team',
  },
  {
    id: 'members',
    label: 'Members',
    icon: <UserCheck size={20} />,
    path: '/team',
  },
  {
    id: 'performance',
    label: 'Performance Dashboard',
    icon: <BarChart3 size={20} />,
    path: '/team/performance',
  },
  {
    id: 'kra-kpi',
    label: 'KRA/KPI Tracking',
    icon: <Target size={20} />,
    path: '/team/kra-kpi',
  },
  {
    id: 'reviews',
    label: 'Reviews',
    icon: <ClipboardCheck size={20} />,
    path: '/reviews',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <TrendingUp size={20} />,
    path: '/team/performance',
  },
];

/**
 * TeamManagementNav
 * Sidebar navigation for team management board
 * Shows KRA/KPI tracking, performance, and analytics sections
 */
const TeamManagementNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/team') {
      return location.pathname === '/team';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Box
      sx={{
        width: 260,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          TEAM MANAGEMENT
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Items */}
      <List sx={{ px: 1, py: 1 }}>
        {navigationItems.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => handleNavigate(item.path)}
            selected={isActive(item.path)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.50',
                color: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
                '&:hover': {
                  bgcolor: 'primary.100',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: isActive(item.path) ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default TeamManagementNav;
